/**
 * FRAME SOLVER — the constraint-based framing core of the parametric kill-cam director (Phase 2).
 *
 * Instead of a hard-coded camera position, an OVERHEAD "spine" beat COMPUTES its pose by satisfying constraints:
 *   • BOTH combatant ships visible inside the exported 9:16 frame ("os dois navios no quadro" — the owner's #1),
 *   • the camera ABOVE the water (a height floor) and OUTSIDE every hull (clip is a HARD constraint),
 *   • framed from the sun-favourable side (front-lit, not silhouetted), with a seed-varied 3/4 kick.
 *
 * The trick that makes it cheap AND guaranteed: the camera is placed ALONG the ship-separation axis (behind one
 * ship, looking through the centroid toward the other) so the two hulls separate in DEPTH — which, for a
 * downward-pitched overhead camera, maps to the TALL (vertical) axis of the portrait 9:16 frame. The pitch is a
 * fixed function of the height ratio, so fitting both ships is a monotone 1-D pull-back: grow the stand-off
 * distance until both project inside the frustum. That always converges and never clips a hull (an overhead camera
 * sits tens of metres above the masts).
 *
 * The frustum primitives (`projectToCam` / `pointInFrame`) are the SAME functions the offline bench scores with —
 * they live here so the client solver and the bench share ONE definition of "inside the clip frame" (the bench
 * re-exports them). PURE of THREE / DOM — unit-tested in framesolver.test.ts.
 */
import { REPLAY } from '@piratas/protocol';

/** The exported clip is a centre-cropped vertical 9:16 — its horizontal field is the vertical FOV × this aspect. */
export const CLIP_ASPECT = REPLAY.OUT_W / REPLAY.OUT_H; // 720/1280 = 0.5625

/** A camera pose (world coords: x east, y up, z = sim-y north; see CLAUDE.md invariant #7). */
export interface Pose { px: number; py: number; pz: number; lx: number; ly: number; lz: number; fov: number; vignette: number; }

/** The world sun azimuth in the sim (x,y) plane — from the renderer's key light `(120, 200, 80)` (worldX, up, worldZ).
 *  worldZ == sim-y, so the horizontal bearing toward the sun is atan2(80, 120). Used to front-light the subjects. */
export const SUN_AZIMUTH = Math.atan2(80, 120);

const NEAR = 0.1;

/** Project world point (wx,wy,wz) into the camera's view basis. `z` > 0 is in front. Right-handed, world-up (0,1,0)
 *  (the solver/finalize guarantee a horizontal look, so the up basis never degenerates). */
export function projectToCam(c: { px: number; py: number; pz: number; lx: number; ly: number; lz: number }, wx: number, wy: number, wz: number): { x: number; y: number; z: number } {
  let fx = c.lx - c.px, fy = c.ly - c.py, fz = c.lz - c.pz;
  const fl = Math.hypot(fx, fy, fz) || 1; fx /= fl; fy /= fl; fz /= fl;
  // right = normalize(forward × worldUp), worldUp = (0,1,0) → (forward × up) = (-fz, 0, fx)
  let rx = -fz, ry = 0, rz = fx;
  const rl = Math.hypot(rx, ry, rz) || 1; rx /= rl; ry /= rl; rz /= rl;
  // up = right × forward (orthonormal, unit)
  const ux = ry * fz - rz * fy, uy = rz * fx - rx * fz, uz = rx * fy - ry * fx;
  const dx = wx - c.px, dy = wy - c.py, dz = wz - c.pz;
  return { x: dx * rx + dy * ry + dz * rz, y: dx * ux + dy * uy + dz * uz, z: dx * fx + dy * fy + dz * fz };
}

/** Is world point (wx,wy,wz) inside the exported clip frustum of camera pose `c`? `marginFrac` shrinks the frame
 *  edges inward (0 = the raw frustum, 0.1 = keep the subject 10 % clear of every edge). Vertical FOV in degrees. */
export function pointInFrame(
  c: { px: number; py: number; pz: number; lx: number; ly: number; lz: number; fov: number },
  wx: number, wy: number, wz: number, aspect = CLIP_ASPECT, marginFrac = 0,
): boolean {
  const p = projectToCam(c, wx, wy, wz);
  if (!(p.z > NEAR)) return false;
  const halfH = p.z * Math.tan((c.fov * Math.PI / 180) / 2) * (1 - marginFrac);
  const halfW = halfH * aspect;
  return Math.abs(p.y) <= halfH && Math.abs(p.x) <= halfW;
}

/** A ship framing target in SIM coords (x east, y north). */
export interface FramePt { x: number; y: number; }

/** Tunable knobs for one overhead solve (the director hands these per beat; the auto-search sweeps them). */
export interface SolveOpts {
  fov: number;         // base vertical FOV (deg)
  fovMax: number;      // widest vertical FOV the solver may open to so BOTH combatants always fit (deg)
  heightFrac: number;  // camera height ÷ stand-off distance → sets the fixed downward pitch (atan(heightFrac))
  heightMin: number;   // absolute floor on camera height above the sea (m)
  heightMax: number;   // absolute CEILING on camera height (m) — bounds how high any overhead flies so cuts to/from
                       //   a low beat never become a huge whip; the pull-back still fits both ships at the capped height
  minDist: number;     // never stand off closer than this (m) — keeps the overhead cam clear + high
  maxDist: number;     // never pull back past this (m) — beyond it the ships are dots but still in-frame
  marginFrac: number;  // keep both ships this fraction clear of the frame edges
  yawBias: number;     // radians of 3/4 kick off the separation axis (× side)
  side: number;        // +1 / -1 — which way the 3/4 kick goes (seed-varied)
  deckLift: number;    // metres above the sea a hull's framing point sits
  lookLift: number;    // metres above the aim sea height the camera aims (compose a touch of sky/waterline)
  lookBias: number;    // 0 = aim at the centroid, 1 = aim at ship A (the sink leans onto the wreck)
  preferredYaw: number;// the STABLE camera bearing (dir from centroid → camera) the director fixed for this beat —
                       //   computed ONCE from the fatal-moment axis + sun, so the standing side NEVER flips mid-beat
                       //   (which would teleport the camera); the ships still fit via the pull-back below.
}

export const DEFAULT_SOLVE: SolveOpts = {
  fov: 55, fovMax: 96, heightFrac: 1.9, heightMin: 30, heightMax: 150, minDist: 26, maxDist: 300,
  marginFrac: 0.12, yawBias: 0.32, side: 1, deckLift: 2, lookLift: 1.5, lookBias: 0, preferredYaw: SUN_AZIMUTH,
};

/**
 * Solve an OVERHEAD pose that keeps ship A and (optional) ship B inside the exported 9:16 frame, above water, from a
 * STABLE bearing, with a seed-varied 3/4 kick. Deterministic. `seaY(x,y)` is the ONE ocean height function.
 *
 * The camera stands at the director-fixed `preferredYaw` (a bearing chosen once from the fatal-moment geometry so it
 * never flips as the ships manoeuvre) and PULLS BACK along a fixed downward pitch until both ships fit — the fit
 * distance is a CONTINUOUS function of the positions, so the per-frame path is smooth (no teleport / no flip).
 *
 * Guarantees (see framesolver.test.ts):
 *   • the camera is above `heightMin` and OUTSIDE both hulls (an overhead cam is tens of metres clear),
 *   • the look keeps a real horizontal component (the pitch is bounded well under vertical),
 *   • BOTH ships fit — the solver pulls back to `maxDist` then OPENS the FOV to `fovMax` until they do; only a
 *     geometrically absurd separation (beyond even that) is returned wide-open (the rare, reported exception).
 */
export function solveTwoShip(A: FramePt, B: FramePt | null, seaY: (x: number, y: number) => number, o: SolveOpts): Pose {
  const bx = B ? B.x : A.x, by = B ? B.y : A.y;
  const cx = (A.x + bx) * 0.5, cy = (A.y + by) * 0.5;        // sim centroid
  // Stand on the STABLE bearing the director fixed for this beat (already sun-favourable + ships-stacked), plus the
  // seed-varied 3/4 kick. No per-frame side choice → the camera can never flip to the opposite side mid-beat.
  const yaw = o.preferredYaw + o.side * o.yawBias;

  const aWY = seaY(A.x, A.y) + o.deckLift;
  const bWY = seaY(bx, by) + o.deckLift;
  // aim point: the centroid, optionally leaned onto ship A (the sink emphasises the wreck without losing the killer)
  const bias = o.lookBias < 0 ? 0 : o.lookBias > 1 ? 1 : o.lookBias;
  const aimX = cx + (A.x - cx) * bias, aimY = cy + (A.y - cy) * bias;
  const lookY = (aWY + bWY) * 0.5 + (aWY - (aWY + bWY) * 0.5) * bias + o.lookLift;

  const out: Pose = { px: 0, py: 0, pz: 0, lx: aimX, ly: lookY, lz: aimY, fov: o.fov, vignette: 0 };
  const place = (hd: number): void => {
    const px = cx + Math.cos(yaw) * hd, pz = cy + Math.sin(yaw) * hd;
    const H = Math.min(o.heightMax, Math.max(o.heightMin, hd * o.heightFrac));
    out.px = px; out.pz = pz; out.py = seaY(px, pz) + H;
    out.lx = aimX; out.ly = lookY; out.lz = aimY;
  };
  // How far the WORST of the two ships sits outside the frame at the current pose+FOV (0 = exactly on the edge, <0 =
  // inside with room, >0 = spilling out). A CONTINUOUS function of the positions — that continuity keeps the per-frame
  // camera path smooth (no teleport / no gimbal-flip spike from a discrete search).
  const worstOut = (fovDeg: number): number => {
    const tanHV = Math.tan((fovDeg * Math.PI / 180) / 2) * (1 - o.marginFrac);
    const frac = (wx: number, wy: number, wz: number): number => {
      const p = projectToCam(out, wx, wy, wz);
      if (!(p.z > NEAR)) return 1e6;
      const halfH = p.z * tanHV, halfW = halfH * CLIP_ASPECT;
      return Math.max(Math.abs(p.y) / halfH, Math.abs(p.x) / halfW) - 1;
    };
    const fa = frac(A.x, aWY, A.y);
    const fb = B ? frac(bx, bWY, by) : -1;
    return Math.max(fa, fb);
  };

  // ── OWNER MANDATE: BOTH combatants (hero + victim) MUST fit an overhead beat — legibility beats proximity. So the
  // solver ESCALATES until they fit: (1) PULL BACK along the axis (which also raises the camera), then if the widest
  // stand-off still can't hold both, (2) OPEN THE FOV up to `fovMax`. Only a geometrically absurd separation (both
  // don't fit even at the widest stand-off + widest FOV) falls through — the rare exception, framed as wide as it can.
  const fovBase = o.fov, lo0 = o.minDist, hi0 = Math.max(o.minDist + 1, o.maxDist);
  place(lo0);
  if (worstOut(fovBase) <= 0) { out.fov = fovBase; return out; } // fits at the closest allowed stand-off
  place(hi0);
  if (worstOut(fovBase) <= 0) {
    // fits somewhere in [minDist, maxDist] at the base FOV → BISECT the (continuous) fitting distance
    let lo = lo0, hi = hi0;
    for (let it = 0; it < 30; it++) { const mid = (lo + hi) * 0.5; place(mid); if (worstOut(fovBase) > 0) lo = mid; else hi = mid; }
    place(hi); out.fov = fovBase; return out;
  }
  // doesn't fit even at the widest stand-off → hold maxDist and OPEN the FOV to make both fit
  place(hi0);
  if (worstOut(o.fovMax) > 0) { out.fov = o.fovMax; return out; } // impossible even wide-open → widest FOV (exception)
  let flo = fovBase, fhi = o.fovMax;
  for (let it = 0; it < 30; it++) { const fm = (flo + fhi) * 0.5; if (worstOut(fm) > 0) flo = fm; else fhi = fm; }
  out.fov = fhi; // smallest FOV that holds both at the widest stand-off
  return out;
}
